
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, MapPin } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type LocationValue = { name: string; latitude: number; longitude: number };

interface LocationInputProps {
  onLocationChange: (location: LocationValue | null) => void;
  initialLocation?: LocationValue | null;
  variant?: 'light' | 'dark';
  /** Homepage hero only. Renders the gold planner CTA and wires Enter to the same path. */
  ctaLabel?: string;
  onCta?: () => void;
  ctaVariant?: 'block' | 'inline';
}

interface PhotonFeature {
  geometry: { coordinates: [number, number] };
  properties: {
    osm_id: number;
    name?: string;
    country?: string;
    state?: string;
    city?: string;
    street?: string;
    housenumber?: string;
  };
}

function formatPhotonResult(feature: PhotonFeature): string {
  const p = feature.properties;
  const parts = [p.name, p.city ?? p.state, p.country].filter(Boolean);
  return parts.join(', ');
}

export const LocationInput: React.FC<LocationInputProps> = ({
  onLocationChange,
  initialLocation,
  variant = 'light',
  ctaLabel,
  onCta,
  ctaVariant = 'block',
}) => {
  const inlineCta = Boolean(ctaLabel) && ctaVariant === 'inline';
  const [locationInput, setLocationInput] = useState(initialLocation?.name || '');
  const [latInput, setLatInput] = useState('');
  const [lonInput, setLonInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<PhotonFeature[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [emptyHint, setEmptyHint] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedLocationRef = useRef<LocationValue | null>(initialLocation ?? null);
  const submittingRef = useRef(false);

  type SearchOutcome =
    | { kind: 'ok'; features: PhotonFeature[] }
    | { kind: 'aborted' }
    | { kind: 'error' };

  const searchLocations = useCallback(async (
    query: string,
    options?: { allowShort?: boolean },
  ): Promise<SearchOutcome> => {
    if (!query.trim() || (query.length < 3 && !options?.allowShort)) {
      setSearchResults([]);
      setShowDropdown(false);
      setNoResults(false);
      return { kind: 'ok', features: [] };
    }

    // Cancel any in-flight typeahead request. Submit clears the debounce first
    // so this abort does not cancel the user's CTA fetch.
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5`,
        { signal }
      );
      const data = await response.json();
      const features: PhotonFeature[] = data.features ?? [];
      setSearchResults(features);
      setShowDropdown(features.length > 0);
      setNoResults(features.length === 0);
      setSearchError(false);
      return { kind: 'ok', features };
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { kind: 'aborted' };
      }
      console.error('Search error:', error);
      setSearchResults([]);
      setShowDropdown(false);
      setNoResults(false);
      setSearchError(true);
      return { kind: 'error' };
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setLocationInput(value);
    setNoResults(false);
    setSearchError(false);
    setEmptyHint(false);
    if (resolvedLocationRef.current && value !== resolvedLocationRef.current.name) {
      resolvedLocationRef.current = null;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchLocations(value).catch(() => {});
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const handleLocationSelect = (feature: PhotonFeature) => {
    const [lon, lat] = feature.geometry.coordinates;
    const location = {
      name: formatPhotonResult(feature),
      latitude: lat,
      longitude: lon,
    };

    setLocationInput(location.name);
    resolvedLocationRef.current = location;
    onLocationChange(location);
    setShowDropdown(false);
    setSearchResults([]);
    setNoResults(false);

    toast({
      title: "Location Selected",
      description: `Successfully selected: ${location.name}`,
    });
  };

  const tryAutoSetCoordinates = useCallback(() => {
    const lat = parseFloat(latInput);
    const lon = parseFloat(lonInput);
    if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
      const location = { name: `${lat.toFixed(4)}, ${lon.toFixed(4)}`, latitude: lat, longitude: lon };
      resolvedLocationRef.current = location;
      onLocationChange(location);
    }
  }, [latInput, lonInput, onLocationChange]);

  const handleCoordinateSubmit = () => {
    const lat = parseFloat(latInput);
    const lon = parseFloat(lonInput);

    if (isNaN(lat) || isNaN(lon)) {
      toast({
        title: "Invalid Coordinates",
        description: "Please enter valid latitude and longitude values.",
        variant: "destructive"
      });
      return;
    }

    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      toast({
        title: "Invalid Range",
        description: "Latitude must be between -90 and 90, longitude between -180 and 180.",
        variant: "destructive"
      });
      return;
    }

    const location = {
      name: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
      latitude: lat,
      longitude: lon
    };
    resolvedLocationRef.current = location;
    onLocationChange(location);
    toast({
      title: "Coordinates Set",
      description: `Location set to: ${location.name}`,
    });
  };

  const submitLocation = async (): Promise<boolean> => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const query = locationInput.trim();
    if (!query) {
      setEmptyHint(true);
      inputRef.current?.focus();
      return false;
    }

    setEmptyHint(false);
    setSearchError(false);

    const resolved = resolvedLocationRef.current;
    if (resolved && (resolved.name === locationInput || resolved.name === query)) {
      onLocationChange(resolved);
      return true;
    }

    if (searchResults.length > 0) {
      handleLocationSelect(searchResults[0]);
      return true;
    }

    const outcome = await searchLocations(query, { allowShort: true });
    if (outcome.kind === 'aborted') return false;
    if (outcome.kind === 'error') return false;
    if (outcome.features[0]) {
      handleLocationSelect(outcome.features[0]);
      return true;
    }

    setNoResults(true);
    return false;
  };

  const handleCta = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      onCta?.();
    } catch {
      // Tracking must never block the planner.
    }
    void submitLocation()
      .then((ok) => {
        if (!ok) submittingRef.current = false;
      })
      .catch(() => {
        submittingRef.current = false;
      });
  };

  return (
    <div className="space-y-4">
      <div>
        {/* City/Address Search with Type-ahead */}
        <div className="space-y-2">
          <div className="relative">
            <div className="relative">
              <Input
                ref={inputRef}
                placeholder="e.g. Moab, Utah or Death Valley, CA"
                value={locationInput}
                aria-describedby={emptyHint ? 'hero-location-empty-hint' : undefined}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) setShowDropdown(true);
                }}
                onBlur={() => {
                  setTimeout(() => setShowDropdown(false), 200);
                }}
                className={cn(
                  'bg-background border-border text-foreground placeholder-muted-foreground',
                  inlineCta && 'h-12 pr-14 text-base md:text-sm',
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setShowDropdown(false);
                  if (e.key === 'Enter' && ctaLabel) {
                    e.preventDefault();
                    handleCta();
                  }
                }}
              />

              {isLoading && (
                <div className={cn('absolute top-1/2 -translate-y-1/2', inlineCta ? 'right-14' : 'right-3')}>
                  <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {inlineCta && (
                <Button
                  type="button"
                  variant="premium"
                  size="icon"
                  aria-label={ctaLabel}
                  onClick={handleCta}
                  className="absolute right-1 top-1 h-10 w-10"
                >
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>

            {/* Results dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((feature) => (
                  <div
                    key={feature.properties.osm_id}
                    className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleLocationSelect(feature);
                    }}
                  >
                    <MapPin className="mr-2 h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                    <span className="truncate text-sm text-gray-900 dark:text-gray-100 font-medium">{formatPhotonResult(feature)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* No results hint */}
            {noResults && !searchError && !isLoading && locationInput.trim().length > 0 && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                No results found — try a different spelling or add the state/country.
              </p>
            )}

            {searchError && locationInput.trim().length > 0 && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Couldn&apos;t reach the location search, try again
              </p>
            )}

            {emptyHint && (
              <p
                id="hero-location-empty-hint"
                aria-live="polite"
                className="mt-1.5 text-xs text-muted-foreground"
              >
                Enter a place to see its best nights
              </p>
            )}
          </div>

          {ctaLabel && !inlineCta && (
            <Button
              type="button"
              variant="premium"
              onClick={handleCta}
              className="w-full sm:w-auto font-semibold"
            >
              {ctaLabel}
            </Button>
          )}
        </div>

        {/* Coordinate Input — collapsed by default */}
        {!showCoordinates ? (
          <button
            type="button"
            onClick={() => setShowCoordinates(true)}
            className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          >
            Use coordinates instead
          </button>
        ) : (
          <div className="space-y-2 mt-3 animate-fade-in">
            <Label className="text-sm text-muted-foreground">Enter coordinates</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  placeholder="Latitude"
                  value={latInput}
                  onChange={(e) => setLatInput(e.target.value)}
                  onBlur={tryAutoSetCoordinates}
                  className="bg-background border-border text-foreground placeholder-muted-foreground"
                  type="number"
                  step="any"
                />
              </div>
              <div>
                <Input
                  placeholder="Longitude"
                  value={lonInput}
                  onChange={(e) => setLonInput(e.target.value)}
                  onBlur={tryAutoSetCoordinates}
                  className="bg-background border-border text-foreground placeholder-muted-foreground"
                  type="number"
                  step="any"
                />
              </div>
            </div>
            <Button
              onClick={handleCoordinateSubmit}
              className={cn(
                "w-full",
                variant === 'dark'
                  ? "bg-white text-slate-900 hover:bg-white/90 font-medium"
                  : ""
              )}
              disabled={!latInput || !lonInput}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Use Coordinates
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
